import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../Utils/axiosInstance";
import { toast } from "react-toastify";

// FETCH MODULES
export const fetchModules = createAsyncThunk(
  "module/fetchModules",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/auth/moduleprocess", {});

      const data = Array.isArray(response.data.items)
        ? response.data.items
        : [];

      toast.success("Modules loaded successfully");

      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch modules";

      toast.error(message);
      return rejectWithValue(message);
    }
  },
);

// FETCH SUB MODULES
export const fetchSubModules = createAsyncThunk(
  "module/fetchSubModules",
  async (moduleName, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/auth/moduleprocess", {
        moduleName,
      });

      const data = Array.isArray(response.data.items)
        ? response.data.items
        : [];

      toast.success("Processes loaded");

      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch sub modules";

      toast.error(message);
      return rejectWithValue(message);
    }
  },
);

// FETCH SUB PROCESS LIST (Cash Flow level)
export const fetchSubProcessList = createAsyncThunk(
  "module/fetchSubProcessList",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/auth/subprocess", {
        ...payload,
        subProcessName: "",
      });

      const data = Array.isArray(response.data.items)
        ? response.data.items
        : [];

      toast.success("Sub Processes loaded");

      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch sub processes";

      toast.error(message);
      return rejectWithValue(message);
    }
  },
);

//  FETCH FINAL DETAILS (HDFC / HSBC)
export const fetchSubProcessDetails = createAsyncThunk(
  "module/fetchSubProcessDetails",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/auth/subprocess", payload);

      toast.success("Process details loaded");

      return response.data.items || {};
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch subprocess details";

      toast.error(message);
      return rejectWithValue(message);
    }
  },
);

//  UPLOAD FILE
export const uploadDataIngestionFile = createAsyncThunk(
  "module/uploadDataIngestionFile",
  async ({ file, fileTypeName, subProcess, Process }, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      formData.append("file", file);
      formData.append("fileTypeName", fileTypeName);
      formData.append("subProcess", subProcess);
      formData.append("Process", Process);

      const response = await axiosInstance.post(
        "/dataIngestion/upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      toast.success("File uploaded successfully");

      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to upload file";

      toast.error(message);

      return rejectWithValue(message);
    }
  },
);

export const sendDataForAudit = createAsyncThunk(
  "module/sendDataForAudit",

  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/dataProcess/senddata",
        payload
      );

      toast.success("Data submitted successfully");

      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to submit data";

      toast.error(message);

      return rejectWithValue(message);
    }
  }
);

const moduleSlice = createSlice({
  name: "module",
  initialState: {
    modules: [],
    subModules: [],
    subProcessList: [],
    subProcessDetails: null,
    uploadResponse: null,
    uploadLoading: false,

    loading: false,
    error: null,
  },
  reducers: {},

  extraReducers: (builder) => {
    builder

      //  MODULES
      .addCase(fetchModules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchModules.fulfilled, (state, action) => {
        state.loading = false;
        state.modules = action.payload;
      })
      .addCase(fetchModules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      //  SUB MODULES
      .addCase(fetchSubModules.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSubModules.fulfilled, (state, action) => {
        state.loading = false;
        state.subModules = action.payload;
      })
      .addCase(fetchSubModules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      //  SUB PROCESS LIST
      .addCase(fetchSubProcessList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSubProcessList.fulfilled, (state, action) => {
        state.loading = false;
        state.subProcessList = action.payload;
      })
      .addCase(fetchSubProcessList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      //  SUB PROCESS DETAILS
      .addCase(fetchSubProcessDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSubProcessDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.subProcessDetails = action.payload;
      })
      .addCase(fetchSubProcessDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      //  UPLOAD FILE
      .addCase(uploadDataIngestionFile.pending, (state) => {
        state.uploadLoading = true;
        state.error = null;
      })

      .addCase(uploadDataIngestionFile.fulfilled, (state, action) => {
        state.uploadLoading = false;
        state.uploadResponse = action.payload;
      })

      .addCase(uploadDataIngestionFile.rejected, (state, action) => {
        state.uploadLoading = false;
        state.error = action.payload;
      });
  },
});

export default moduleSlice.reducer;
